import type { ButtonHTMLAttributes, ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@shared/utils";

interface SecondaryButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  /** Whether to show a chevron-down icon after the text */
  withChevron?: boolean;
  isActive?: boolean;
}

/**
 * Secondary button atom.
 * Outlined style with rounded corners, used for filters and secondary actions.
 * Border and text color: --color-secondary-120
 */
export function SecondaryButton({
  children,
  withChevron = false,
  className,
  isActive = false,
  ...props
}: SecondaryButtonProps) {
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center rounded-full border",
        "px-[23px] py-[12px] cursor-pointer transition-colors",
        isActive
          ? "border-primary-110 bg-secondary-120/20 hover:bg-secondary-120/30"
          : "border-secondary-120 bg-transparent hover:bg-secondary-120/10",
        className,
      )}
      {...props}
    >
      <span
        className={cn(
          "text-h5 font-semibold",
          isActive ? "text-primary-110" : "text-secondary-120",
        )}
      >
        {children}
      </span>
      {withChevron && (
        <ChevronDown
          size={16}
          className={cn(
            "ml-2",
            isActive ? "text-primary-110" : "text-secondary-120",
          )}
        />
      )}
    </button>
  );
}
